import { useState, useEffect } from "react";
import { StokesTransaction, StokesBalance } from "@/common/model/Stokes";
import { StokesService } from "@/services/StokesService";

interface StokesTabProps {
    userId: number;
}

const formatDateTime = (dateString: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
};

const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
};

export const StokesTab: React.FC<StokesTabProps> = ({ userId }) => {
    const [transactions, setTransactions] = useState<StokesTransaction[]>([]);
    const [balance, setBalance] = useState<StokesBalance>({ total: 0, earned: 0, spent: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [adjustAction, setAdjustAction] = useState<"earn" | "spend">("earn");
    const [adjustAmount, setAdjustAmount] = useState<string>("");
    const [isAdjusting, setIsAdjusting] = useState(false);

    const fetchStokes = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await StokesService.fetchByUserId(userId);
            setTransactions(data);
            setBalance(StokesService.calculateBalance(data));
        } catch (err: any) {
            setError(err.message || "Failed to fetch stokes data");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStokes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleAdjust = async () => {
        const amount = parseInt(adjustAmount, 10);
        if (isNaN(amount) || amount <= 0) {
            return;
        }

        setIsAdjusting(true);
        try {
            await StokesService.adjust({
                userId,
                action: adjustAction,
                stokes: amount,
            });
            setShowAdjustModal(false);
            setAdjustAmount("");
            await fetchStokes();
        } catch (err: any) {
            setError(err.message || "Failed to adjust stokes");
        } finally {
            setIsAdjusting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="relative">
                    <div className="w-8 h-8 rounded-full border-3 border-gray-200"></div>
                    <div className="absolute top-0 left-0 w-8 h-8 rounded-full border-3 border-cyan-500 border-t-transparent animate-spin"></div>
                </div>
                <p className="ml-3 text-sm text-gray-600">Loading stokes data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Balance Summary */}
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-medium text-gray-600">Current Balance</h3>
                        <p className="text-3xl font-bold text-cyan-700">{formatNumber(balance.total)}</p>
                    </div>
                    <div className="text-right space-y-1">
                        <div className="text-xs text-gray-500">
                            <span className="text-green-600 font-medium">+{formatNumber(balance.earned)}</span> earned
                        </div>
                        <div className="text-xs text-gray-500">
                            <span className="text-red-600 font-medium">-{formatNumber(balance.spent)}</span> spent
                        </div>
                    </div>
                    <button
                        onClick={() => setShowAdjustModal(true)}
                        className="px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors"
                    >
                        + Adjust Stokes
                    </button>
                </div>
            </div>

            {/* Transactions Table */}
            <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Transaction History</h3>
                {transactions.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No transactions found</p>
                ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date & Time
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        From User
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Reason
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Engagement
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {transactions.map((transaction) => (
                                    <tr key={transaction.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                            {formatDateTime(transaction.date)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    transaction.type === "earn"
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-red-100 text-red-800"
                                                }`}
                                            >
                                                {transaction.type === "earn" ? "↑" : "↓"}{" "}
                                                {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`text-sm font-medium ${
                                                    transaction.type === "earn" ? "text-green-600" : "text-red-600"
                                                }`}
                                            >
                                                {transaction.type === "earn" ? "+" : "-"}
                                                {formatNumber(transaction.amount)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {transaction.earnedFromUserId ? (
                                                <div className="space-y-1">
                                                    <div>
                                                        {transaction.earnedFromUserName || "Unknown"}
                                                        <span className="text-gray-400 text-xs ml-1">
                                                            (id:{transaction.earnedFromUserId})
                                                        </span>
                                                    </div>
                                                    {transaction.earnedFromUserEmail && (
                                                        <div className="text-xs text-gray-500">
                                                            {transaction.earnedFromUserEmail}
                                                        </div>
                                                    )}
                                                    {transaction.earnedFromUserSessionCount !== null && (
                                                        <div className="text-xs text-gray-500">
                                                            Sessions: {transaction.earnedFromUserSessionCount}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs">
                                                {transaction.reason}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-center">
                                            {transaction.isEngagement ? (
                                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600">
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Adjust Modal */}
            {showAdjustModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Adjust Stokes for User #{userId}</h3>

                        <div className="space-y-4">
                            {/* Action Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="action"
                                            value="earn"
                                            checked={adjustAction === "earn"}
                                            onChange={() => setAdjustAction("earn")}
                                            className="mr-2 text-cyan-600 focus:ring-cyan-500"
                                        />
                                        <span className="text-sm text-gray-700">Earn</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="action"
                                            value="spend"
                                            checked={adjustAction === "spend"}
                                            onChange={() => setAdjustAction("spend")}
                                            className="mr-2 text-cyan-600 focus:ring-cyan-500"
                                        />
                                        <span className="text-sm text-gray-700">Spend</span>
                                    </label>
                                </div>
                            </div>

                            {/* Amount Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={adjustAmount}
                                    onChange={(e) => setAdjustAmount(e.target.value)}
                                    placeholder="Enter amount"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                                />
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAdjustModal(false);
                                    setAdjustAmount("");
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                disabled={isAdjusting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAdjust}
                                disabled={isAdjusting || !adjustAmount || parseInt(adjustAmount, 10) <= 0}
                                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isAdjusting ? "Applying..." : "Apply"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
